// ============================================================================
// LE CODE COMMERÇANT NEPTING DE CHAQUE CAISSE — ce que la Console décide, ce que la caisse envoie au Login.
//
// UTILITÉ : une caisse qui encaisse par l'application Nepting (App2App) ouvre sa session par un `Login` qui porte le
// code commerçant (`MERCHANT_CODE`) et, au besoin, l'adresse du serveur Nepting (`WEB_SERVICE_URL`). Sans eux, la
// caisse ne peut que compter sur la configuration faite à la main dans l'application Nepting.
//
// ⚠️ UN CODE PAR CAISSE, JAMAIS UN PAR MAGASIN (décision utilisateur) : des magasins encaissent déjà sur
//    plusieurs TPE rattachés à des contrats différents. La clé est le n° de caisse sous la forme que la caisse scelle
//    (« 01 »), pas la tablette : une tablette remplacée reprend le code de sa caisse.
// ⚠️ LA CONSOLE DÉCIDE, LA BASE DU MAGASIN PORTE, LE BACK-OFFICE LIT : le réglage est `bo_only`, écrit par la seule
//    Console (réservé au staff de mise en service), refusé à l'écriture par le back-office, et la caisse ne fait que le
//    lire. Il vit dans le contrat parce que les trois doivent lire et juger la même valeur de la même façon.
// ⚠️ UNE SEULE LIGNE POUR TOUTES LES CAISSES (un objet JSON par n° de caisse) : la table `setting` n'a que le nom pour
//    clé. La Console réécrit l'état COMPLET à chaque changement, donc une caisse ne peut pas effacer celle d'à côté.
// ============================================================================

/** Le réglage qui porte les codes commerçants Nepting du magasin, par n° de caisse. */
export const NEPTING_TERMINALS_SETTING = 'tpe.nepting_terminals';

/** Ce que la caisse envoie au Login Nepting. */
export interface NeptingTerminal {
  /** `MERCHANT_CODE` — numérique, jusqu'à 32 chiffres (spécification App2App). */
  merchantCode: string;
  /** `WEB_SERVICE_URL` — l'adresse du serveur Nepting ; `null` : celle que l'application Nepting connaît déjà. */
  webServiceUrl: string | null;
}

/** Les codes du magasin, par n° de caisse (« 01 »). */
export type NeptingTerminals = Readonly<Record<string, NeptingTerminal>>;

/** Longueur maximale de `WEB_SERVICE_URL` (spécification App2App : ANS..134). */
export const NEPTING_URL_MAX = 134;

/** Une adresse https : un hôte, un port facultatif, puis un chemin sans espace (`https://qualif.nepting.com:443/nepweb/ws?wsdl`). */
const NEPTING_URL_RE = /^https:\/\/[A-Za-z0-9.-]+(?::[0-9]{1,5})?(?:[/?#]\S*)?$/;

/**
 * Le n° de caisse tel que la caisse le scelle — deux chiffres au moins, jamais zéro.
 * ⚠️ « 1 » N'EST PAS ACCEPTÉ : la caisse se cherche sous « 01 » et ne trouverait jamais son code.
 */
export function isNeptingRegisterKey(registerId: string): boolean {
  return /^(?:0[1-9]|[1-9][0-9]{1,3})$/.test(registerId);
}

/** Le code commerçant tel qu'il s'enregistre : sans les espaces que la saisie a pu porter. */
export function normalizeMerchantCode(v: string): string {
  return v.replace(/\s+/g, '');
}

/**
 * CE QUI EMPÊCHE D'ENREGISTRER UN CODE — vide si rien.
 * ⚠️ L'ADRESSE EST EN HTTPS : c'est celle du serveur qui autorise les paiements, et rien ne justifie qu'elle circule en
 *    clair. Une adresse vide est permise : l'application Nepting garde alors la sienne.
 */
export function neptingTerminalProblems(t: { merchantCode: string; webServiceUrl: string | null }): string[] {
  const problems: string[] = [];
  const code = normalizeMerchantCode(t.merchantCode);
  if (!/^[0-9]{1,32}$/.test(code)) problems.push(`code commerçant « ${t.merchantCode} » : des chiffres seulement, 32 au plus`);
  const url = t.webServiceUrl?.trim() ?? '';
  if (url) {
    if (url.length > NEPTING_URL_MAX) problems.push(`adresse du serveur Nepting : ${NEPTING_URL_MAX} caractères au plus`);
    // ⚠️ UN MOTIF ET NON `new URL()` : le contrat se compile aussi pour la caisse et le client de synchronisation, qui
    //    n'ont pas le global `URL` dans leurs types — la porte de typage l'a refusé.
    if (!NEPTING_URL_RE.test(url)) problems.push(`adresse du serveur Nepting « ${url} » : une adresse https:// attendue`);
  }
  return problems;
}

/** Ce que la lecture du réglage a donné : les codes lisibles, et ce qui ne l'était pas — jamais tu. */
export interface NeptingTerminalsReading {
  terminals: NeptingTerminals;
  /** `null` : tout a été lu. Sinon, ce qui a été écarté et pourquoi. */
  problem: string | null;
  /** Les n° de caisse dont l'entrée a été écartée — une caisse sait ainsi si le problème la concerne. */
  rejected: readonly string[];
  /** La valeur entière n'a pas pu être lue : on ignore alors quelles caisses avaient un code. */
  unreadable: boolean;
}

/**
 * Lit le réglage tel que la base le porte.
 * ⚠️ ABSENT OU VIDE = AUCUN CODE, sans problème : c'est l'état de tout magasin avant que la Console en pose un.
 * ⚠️ ILLISIBLE N'EST PAS « AUCUN CODE » : une entrée écartée est NOMMÉE, sinon la caisse se croirait configurée dans
 *    l'application Nepting alors que la Console lui a donné un code qu'elle n'a pas su lire.
 */
export function parseNeptingTerminals(value: string | null | undefined): NeptingTerminalsReading {
  const raw = (value ?? '').trim();
  if (!raw) return { terminals: {}, problem: null, rejected: [], unreadable: false };
  let doc: unknown;
  try { doc = JSON.parse(raw); } catch (e) {
    return { terminals: {}, problem: `réglage ${NEPTING_TERMINALS_SETTING} illisible : ${(e as Error).message}`, rejected: [], unreadable: true };
  }
  if (!doc || typeof doc !== 'object' || Array.isArray(doc)) {
    return { terminals: {}, problem: `réglage ${NEPTING_TERMINALS_SETTING} illisible : un objet par n° de caisse attendu`, rejected: [], unreadable: true };
  }
  const terminals: Record<string, NeptingTerminal> = {};
  const rejected: string[] = [];
  const rejectedIds: string[] = [];
  for (const [registerId, entry] of Object.entries(doc as Record<string, unknown>)) {
    const e = entry as { merchantCode?: unknown; webServiceUrl?: unknown } | null;
    const merchantCode = typeof e?.merchantCode === 'string' ? e.merchantCode : '';
    const webServiceUrl = typeof e?.webServiceUrl === 'string' && e.webServiceUrl.trim() ? e.webServiceUrl.trim() : null;
    const problems = isNeptingRegisterKey(registerId)
      ? neptingTerminalProblems({ merchantCode, webServiceUrl })
      : [`n° de caisse « ${registerId} » invalide`];
    if (problems.length) { rejected.push(`caisse ${registerId} : ${problems.join(', ')}`); rejectedIds.push(registerId); }
    else terminals[registerId] = { merchantCode: normalizeMerchantCode(merchantCode), webServiceUrl };
  }
  return { terminals, problem: rejected.length ? `entrées écartées — ${rejected.join(' ; ')}` : null, rejected: rejectedIds, unreadable: false };
}

/**
 * Écrit le réglage — clés triées, donc la même valeur pour le même état (une relance ne change rien).
 * ⚠️ L'adresse absente n'est pas écrite : `null` et « absente » disent la même chose, une seule forme suffit.
 */
export function serializeNeptingTerminals(terminals: NeptingTerminals): string {
  const out: Record<string, { merchantCode: string; webServiceUrl?: string }> = {};
  for (const registerId of Object.keys(terminals).sort()) {
    const t = terminals[registerId]!;
    out[registerId] = t.webServiceUrl ? { merchantCode: t.merchantCode, webServiceUrl: t.webServiceUrl } : { merchantCode: t.merchantCode };
  }
  return JSON.stringify(out);
}

/** Les codes après avoir posé (ou retiré, `null`) celui d'une caisse. */
export function withNeptingTerminal(terminals: NeptingTerminals, registerId: string, entry: NeptingTerminal | null): NeptingTerminals {
  const next: Record<string, NeptingTerminal> = { ...terminals };
  if (entry) next[registerId] = { merchantCode: normalizeMerchantCode(entry.merchantCode), webServiceUrl: entry.webServiceUrl?.trim() || null };
  else delete next[registerId];
  return next;
}
